// Voice recognition service for workshop interface
// Supports wake word detection and speech-to-text
//
// This module provides two implementations:
// 1. VoiceService - Uses browser's native Web Speech API (default, works everywhere)
// 2. SherpaVoiceService - Uses Sherpa-ONNX WASM (open source, fully offline)
//
// The Sherpa service is preferred when available, falling back to Web Speech API.

import type { VoiceIntent, VoiceState, ComponentCategory, ConditionState } from '@/types';

// Enable for demo tuning - shows intent parsing details in console
const DEBUG_INTENT_PARSING = false;

// Confidence thresholds for intent quality
const CONFIDENCE_HIGH = 0.8;    // Green checkmark, proceed automatically
const CONFIDENCE_OK = 0.5;      // Yellow warning, may need confirmation
const CONFIDENCE_LOW = 0.3;     // Red warning, suggest rephrasing

export { CONFIDENCE_HIGH, CONFIDENCE_OK, CONFIDENCE_LOW };

// Re-export Sherpa voice service for direct use
export { SherpaVoiceService, getSherpaVoiceService } from './sherpa-voice';
export type { SherpaVoiceConfig } from './sherpa-voice';

const WAKE_WORD = 'hey ventura';
// Phonetic alternatives for speech recognition tolerance
// "Ventura" can be misheard as "venture", "van tura", etc.
// Using regex patterns for more precise matching (avoid false positives like "aventura")
const WAKE_WORD_PATTERNS = [
  /\bhey ventura\b/i,
  /\bhey venture\b/i,
  /\bhey van tura\b/i,
  /\bhi ventura\b/i,
  /\bhey vintura\b/i,
  /\bhaventura\b/i,      // Speech recognition sometimes joins "hey ventura" into one word
  /\bheyventura\b/i,     // Another joined variant
];

/**
 * Voice service configuration
 */
export interface VoiceConfig {
  wakeWord?: string;
  language?: string;
  continuous?: boolean;
  followUpDurationMs?: number; // How long to stay in command mode after wake word (ms)
  onStateChange?: (state: VoiceState) => void;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  onIntent?: (intent: VoiceIntent) => void;
  onError?: (error: Error) => void;
}

/**
 * Voice recognition service class
 */
// Follow-up window duration (ms) - allow commands without wake word after initial wake
const FOLLOW_UP_WINDOW_MS = 5000;

export class VoiceService {
  private recognition: SpeechRecognition | null = null;
  private config: VoiceConfig;
  private state: VoiceState = 'idle';
  private isListeningForCommand = false;
  private permissionGranted = false;
  private explicitlyStopped = false;
  private followUpTimeout: ReturnType<typeof setTimeout> | null = null;
  private interimCommandTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingInterimText = '';
  private lastProcessedTranscript = '';

  constructor(config: VoiceConfig = {}) {
    // Check localStorage for previous permission grant
    if (typeof window !== 'undefined') {
      this.permissionGranted = localStorage.getItem('voicePermissionGranted') === 'true';
    }
    this.config = {
      wakeWord: WAKE_WORD,
      language: 'en-US',
      continuous: true,
      ...config,
    };
  }

  /**
   * Check network connectivity
   */
  private isOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  /**
   * Initialize speech recognition
   */
  initialize(): boolean {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser');
      return false;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.lang = this.config.language!;
    this.recognition.continuous = this.config.continuous!;
    this.recognition.interimResults = true;

    this.recognition.onstart = this.handleStart.bind(this);
    this.recognition.onresult = this.handleResult.bind(this);
    this.recognition.onerror = this.handleError.bind(this);
    this.recognition.onend = this.handleEnd.bind(this);

    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (this.state === 'error') {
          this.setState('idle');
        }
      });
      window.addEventListener('offline', () => {
        this.setState('error');
        this.config.onError?.(new Error('offline'));
      });
    }

    return true;
  }

  /**
   * Start listening for wake word
   */
  startListening(): void {
    console.log('VoiceService.startListening called, recognition:', !!this.recognition);
    this.explicitlyStopped = false;

    // Check network connectivity before starting
    if (!this.isOnline()) {
      this.setState('error');
      this.config.onError?.(new Error('offline'));
      return;
    }

    if (!this.recognition) {
      console.log('No recognition, initializing...');
      if (!this.initialize()) {
        console.log('Initialize failed');
        return;
      }
    }

    this.setState('listening-wake');
    console.log('Calling recognition.start()');
    try {
      this.recognition?.start();
      console.log('recognition.start() called successfully');
    } catch (err) {
      console.error('recognition.start() error:', err);
    }
  }

  /**
   * Stop listening
   */
  stopListening(): void {
    console.log('VoiceService.stopListening called');
    this.explicitlyStopped = true;
    this.isListeningForCommand = false;
    this.clearFollowUpWindow();

    // Flush any pending interim command before stopping
    // This prevents losing commands in PTT mode when user releases the key
    if (this.interimCommandTimer && this.pendingInterimText) {
      const pendingText = this.pendingInterimText;
      this.cancelInterimCommand();
      const intent = this.parseIntent(pendingText);
      this.config.onIntent?.(intent);
    }
    this.cancelInterimCommand();
    this.lastProcessedTranscript = '';

    this.setState('idle');

    // Use abort() instead of stop() for immediate termination
    // This prevents any pending results from triggering restarts
    try {
      this.recognition?.abort();
    } catch (err) {
      // Fallback to stop if abort fails
      this.recognition?.stop();
    }
  }

  /**
   * Activate push-to-talk mode - skip wake word and go directly to command listening
   */
  activatePushToTalk(): void {
    console.log('VoiceService.activatePushToTalk called');
    this.explicitlyStopped = false;

    // Check network connectivity before starting
    if (!this.isOnline()) {
      this.setState('error');
      this.config.onError?.(new Error('offline'));
      return;
    }

    if (!this.recognition) {
      if (!this.initialize()) {
        console.log('Initialize failed');
        return;
      }
    }

    // Skip wake word - go directly to command listening
    this.isListeningForCommand = true;
    this.setState('listening-command');

    // Start recognition if not already running
    try {
      this.recognition?.start();
      console.log('Push-to-talk recognition started');
    } catch (err) {
      // Already running - that's fine, just set the state
      console.log('Recognition already running, continuing in command mode');
    }
  }

  /**
   * Handle recognition start - track permission granted
   */
  private handleStart(): void {
    console.log('Speech recognition started, permission granted');
    this.permissionGranted = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('voicePermissionGranted', 'true');
    }
  }

  /**
   * Handle speech recognition results
   * Uses debounced interim processing for faster command response
   */
  private handleResult(event: SpeechRecognitionEvent): void {
    const results = event.results;
    const lastResult = results[results.length - 1];
    const transcript = lastResult[0].transcript.toLowerCase().trim();
    const isFinal = lastResult.isFinal;

    this.config.onTranscript?.(transcript, isFinal);

    if (!this.isListeningForCommand) {
      // Check for wake word using regex patterns for precise matching
      const hasWakeWord = WAKE_WORD_PATTERNS.some((pattern) =>
        pattern.test(transcript)
      );

      if (hasWakeWord) {
        this.isListeningForCommand = true;
        this.setState('listening-command');
        this.startFollowUpWindow();

        // Check if there's a command after the wake word
        let commandPart = transcript;
        WAKE_WORD_PATTERNS.forEach((pattern) => {
          commandPart = commandPart.replace(pattern, '').trim();
        });

        if (commandPart.length > 0) {
          // Process inline command (debounced for interim, immediate for final)
          this.processCommand(transcript, isFinal);
        }
        return;
      }
    } else {
      // In command mode — process via debounced interim or immediate final
      this.processCommand(transcript, isFinal);
    }
  }

  /**
   * Start/reset the follow-up window timer
   * Allows quick follow-up commands without wake word for a few seconds
   */
  private startFollowUpWindow(): void {
    // Clear any existing timeout
    if (this.followUpTimeout) {
      clearTimeout(this.followUpTimeout);
    }

    // Use configured duration or default
    const duration = this.config.followUpDurationMs || FOLLOW_UP_WINDOW_MS;

    // Set new timeout to return to wake-word listening
    this.followUpTimeout = setTimeout(() => {
      this.isListeningForCommand = false;
      this.setState('listening-wake');
      this.followUpTimeout = null;
    }, duration);
  }

  /**
   * Update the follow-up duration (called when settings change)
   */
  setFollowUpDuration(durationMs: number): void {
    this.config.followUpDurationMs = durationMs;
  }

  /**
   * Clear the follow-up window (e.g., when stopping)
   */
  private clearFollowUpWindow(): void {
    if (this.followUpTimeout) {
      clearTimeout(this.followUpTimeout);
      this.followUpTimeout = null;
    }
  }

  /**
   * Process a voice command (from wake word inline or follow-up)
   * Handles both final and interim results with debouncing for faster response
   */
  private processCommand(transcript: string, isFinal: boolean): void {
    if (isFinal) {
      this.cancelInterimCommand();

      // Skip if identical to what we already processed from interim
      if (transcript === this.lastProcessedTranscript) {
        this.lastProcessedTranscript = '';
        this.startFollowUpWindow();
        this.setState('listening-command');
        return;
      }
      this.lastProcessedTranscript = '';

      this.setState('processing');
      const intent = this.parseIntent(transcript);
      this.config.onIntent?.(intent);
      this.startFollowUpWindow();
      this.setState('listening-command');
    } else {
      // Debounce interim results — fires once text stabilizes for 300ms
      this.scheduleInterimCommand(transcript);
    }
  }

  /**
   * Schedule processing of an interim result after a stability delay.
   * Only resets the timer if the text changed, so stable text fires quickly.
   */
  private scheduleInterimCommand(transcript: string): void {
    // If text hasn't changed, let the existing timer run
    if (transcript === this.pendingInterimText && this.interimCommandTimer) {
      return;
    }

    this.cancelInterimCommand();
    this.pendingInterimText = transcript;

    this.interimCommandTimer = setTimeout(() => {
      this.interimCommandTimer = null;
      this.lastProcessedTranscript = transcript;
      this.setState('processing');
      const intent = this.parseIntent(transcript);
      this.config.onIntent?.(intent);
      this.startFollowUpWindow();
      this.setState('listening-command');
    }, 300);
  }

  /**
   * Cancel pending interim command processing
   */
  private cancelInterimCommand(): void {
    if (this.interimCommandTimer) {
      clearTimeout(this.interimCommandTimer);
      this.interimCommandTimer = null;
    }
    this.pendingInterimText = '';
  }

  /**
   * Handle recognition errors
   */
  private handleError(event: SpeechRecognitionErrorEvent): void {
    console.log('Speech recognition error:', event.error);

    // Don't treat 'aborted' as an error - it's expected when stopping
    if (event.error === 'aborted') {
      this.setState('idle');
      return;
    }

    // Don't treat 'no-speech' as error, just go back to listening
    if (event.error === 'no-speech') {
      this.setState('listening-wake');
      return;
    }

    // Map Web Speech API errors to user-friendly types
    let errorMessage: string = event.error;
    switch (event.error) {
      case 'network':
        errorMessage = 'offline';
        break;
      case 'not-allowed':
      case 'service-not-allowed':
        errorMessage = 'permission-denied';
        break;
      case 'audio-capture':
        errorMessage = 'microphone-unavailable';
        break;
      case 'language-not-supported':
        errorMessage = 'language-error';
        break;
    }

    this.setState('error');
    this.config.onError?.(new Error(errorMessage));
  }

  /**
   * Handle recognition end
   */
  private handleEnd(): void {
    console.log('Speech recognition ended, state:', this.state, 'explicitlyStopped:', this.explicitlyStopped);

    // Don't restart if explicitly stopped by user (PTT release, manual stop, etc.)
    if (this.explicitlyStopped) {
      console.log('Not restarting - explicitly stopped');
      this.setState('idle');
      return;
    }

    // Don't restart if already idle (avoid loops)
    if (this.state === 'idle') {
      console.log('Not restarting - already idle');
      return;
    }

    // Don't restart if in command mode (PTT) - user controls when to stop
    if (this.isListeningForCommand) {
      console.log('Not restarting - in PTT/command mode, waiting for explicit stop');
      return;
    }

    // Auto-restart if continuous mode - persistent listening for kiosk (wake word mode only)
    if (this.config.continuous && this.permissionGranted) {
      console.log('Restarting in continuous mode (persistent listening)');

      // If we were processing or in command mode, go back to listening-wake
      if (this.state === 'processing' || this.state === 'listening-command') {
        this.setState('listening-wake');
      } else if (this.state === 'error') {
        this.setState('listening-wake');
      }

      // Small delay to avoid rapid restarts
      setTimeout(() => {
        // Double-check we still want to restart
        if (this.explicitlyStopped || this.state === 'idle') {
          console.log('Restart cancelled - state changed during delay');
          return;
        }
        try {
          this.recognition?.start();
          console.log('Recognition restarted successfully');
        } catch (err) {
          console.log('Could not restart:', err);
          // Try again after a longer delay
          setTimeout(() => {
            if (!this.explicitlyStopped && this.state !== 'idle') {
              try {
                this.recognition?.start();
                console.log('Second restart attempt succeeded');
              } catch (e) {
                console.log('Second restart attempt failed:', e);
              }
            }
          }, 500);
        }
      }, 100);
    }
  }

  /**
   * Update state and notify listeners
   */
  private setState(state: VoiceState): void {
    this.state = state;
    this.config.onStateChange?.(state);
  }

  /**
   * Parse natural language into structured intent
   */
  parseIntent(transcript: string): VoiceIntent {
    const lowerTranscript = transcript.toLowerCase();

    // Remove wake word from transcript using regex patterns
    let cleanTranscript = lowerTranscript;
    WAKE_WORD_PATTERNS.forEach((pattern) => {
      cleanTranscript = cleanTranscript.replace(pattern, '').trim();
    });

    // Quick patterns for common demo phrases
    // "show me the [component]" or "show me the [component] [condition]"
    const showMePattern = /show (?:me )?(?:the )?(\w+)(?:\s+(\w+))?/;
    const showMeMatch = cleanTranscript.match(showMePattern);

    // Pre-extracted component from "show me the X" pattern (used below)
    let showMeComponent: string | undefined;
    if (showMeMatch) {
      showMeComponent = showMeMatch[1]; // First word after "show me the"
      if (DEBUG_INTENT_PARSING) {
        console.log('[Show Me Pattern]', { match: showMeMatch, component: showMeComponent });
      }
    }

    // Detect intent type
    let type: VoiceIntent['type'] = 'search';
    let timeRange: VoiceIntent['timeRange'] | undefined;
    let proofTarget: string | undefined;
    let overlapWith: string | undefined;

    // Workshop Memory - Temporal queries (check first, higher priority)
    if (
      cleanTranscript.includes('what did we do') ||
      cleanTranscript.includes('what happened') ||
      cleanTranscript.includes('show me') && (
        cleanTranscript.includes('march') ||
        cleanTranscript.includes('april') ||
        cleanTranscript.includes('may') ||
        cleanTranscript.includes('last month') ||
        cleanTranscript.includes('last week')
      )
    ) {
      type = 'period';
      timeRange = this.extractTimeRange(cleanTranscript);
    } else if (
      cleanTranscript.includes('timeline') ||
      cleanTranscript.includes('show timeline') ||
      cleanTranscript.includes('show me the timeline')
    ) {
      type = 'timeline';
    } else if (
      cleanTranscript.includes('prove') ||
      cleanTranscript.includes('proof')
    ) {
      type = 'prove';
      proofTarget = this.extractProofTarget(cleanTranscript);
    } else if (
      cleanTranscript.includes('when did') ||
      cleanTranscript.includes('when was')
    ) {
      type = 'when';
    } else if (
      cleanTranscript.includes('what changed') ||
      cleanTranscript.includes('what\'s changed')
    ) {
      type = 'what-changed';
    } else if (
      cleanTranscript.includes('overlap') ||
      cleanTranscript.includes('during')
    ) {
      type = 'overlap';
      overlapWith = this.extractComponent(cleanTranscript) || undefined;
    } else if (
      cleanTranscript.includes('compare') ||
      cleanTranscript.includes('before and after')
    ) {
      type = 'compare';
    } else if (
      cleanTranscript.includes('more like this') ||
      cleanTranscript.includes('similar')
    ) {
      type = 'similar';
    } else if (
      cleanTranscript.includes('go back') ||
      cleanTranscript.includes('exit') ||
      cleanTranscript.includes('home')
    ) {
      type = 'navigate';
    }

    // Image selection by number: "image one", "image 1", "number 2", "select 3", etc.
    let imageNumber: number | undefined;
    const numberWords: Record<string, number> = {
      'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
      'six': 6, 'seven': 7, 'eight': 8, 'nine': 9,
      '1': 1, '2': 2, '3': 3, '4': 4, '5': 5,
      '6': 6, '7': 7, '8': 8, '9': 9,
    };

    // Pattern: "image one", "image 1", "number one", "select one", "look at one", "open one"
    const selectPatterns = [
      /(?:image|number|select|look at|open|show)\s+(\w+)/i,
      /^(\w+)$/i, // Just a number word by itself
    ];

    for (const pattern of selectPatterns) {
      const match = cleanTranscript.match(pattern);
      if (match) {
        const numWord = match[1].toLowerCase();
        if (numberWords[numWord]) {
          imageNumber = numberWords[numWord];
          type = 'select';
          break;
        }
      }
    }

    // Extract component
    const component = this.extractComponent(cleanTranscript);

    // Extract condition
    const condition = this.extractCondition(cleanTranscript);

    // Calculate confidence based on match quality
    let confidence = 0.3; // Base confidence for any parsed input

    // Component match adds significant confidence
    if (component) {
      confidence += 0.3;
    }

    // Condition match adds confidence
    if (condition) {
      confidence += 0.25;
    }

    // Specific intent type (not generic search) adds confidence
    if (type !== 'search') {
      confidence += 0.15;
    }

    // Both component AND condition is high confidence
    if (component && condition) {
      confidence += 0.1; // Bonus for complete query
    }

    // Cap at 1.0
    confidence = Math.min(confidence, 1.0);

    if (DEBUG_INTENT_PARSING) {
      console.log('[Intent Parse]', {
        raw: transcript,
        clean: cleanTranscript,
        type,
        component,
        condition,
        confidence,
      });
    }

    return {
      type,
      query: cleanTranscript,
      component,
      condition,
      confidence,
      rawTranscript: transcript,
      // Image selection
      imageNumber,
      // Workshop Memory fields
      timeRange,
      proofTarget,
      overlapWith,
    };
  }

  /**
   * Generate suggestions for improving voice query
   * Called when confidence is below CONFIDENCE_OK threshold
   */
  getSuggestions(intent: VoiceIntent): string[] {
    const suggestions: string[] = [];

    // If no component detected, suggest common ones
    if (!intent.component) {
      suggestions.push('Try: "Show me the engine"');
      suggestions.push('Try: "Show me the interior"');
    }

    // If no condition detected, suggest adding one
    if (!intent.condition && intent.component) {
      suggestions.push(`Try: "Show me the ${intent.component} restored"`);
      suggestions.push(`Try: "Show me the ${intent.component} before"`);
    }

    // If both missing, suggest complete phrase
    if (!intent.component && !intent.condition) {
      suggestions.push('Try: "Show me the engine restored"');
    }

    // Generic fallback
    if (suggestions.length === 0) {
      suggestions.push('Speak clearly and try again');
    }

    return suggestions.slice(0, 2); // Max 2 suggestions
  }

  /**
   * Extract time range from transcript for memory queries
   */
  private extractTimeRange(transcript: string): VoiceIntent['timeRange'] | undefined {
    const months = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];

    for (const month of months) {
      if (transcript.includes(month)) {
        return { relative: month };
      }
    }

    if (transcript.includes('last month')) {
      return { relative: 'last month' };
    }
    if (transcript.includes('last week')) {
      return { relative: 'last week' };
    }
    if (transcript.includes('early')) {
      return { relative: 'early' };
    }
    if (transcript.includes('late')) {
      return { relative: 'late' };
    }

    return undefined;
  }

  /**
   * Extract proof target from "prove we replaced..." queries
   */
  private extractProofTarget(transcript: string): string | undefined {
    // Common patterns: "prove we replaced the floor pans"
    const patterns = [
      /prove (?:we |that we )?(?:replaced|fixed|rebuilt|installed|repaired) (?:the )?(.+)/,
      /proof (?:of |for )?(?:the )?(.+)/,
    ];

    for (const pattern of patterns) {
      const match = transcript.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    return undefined;
  }

  /**
   * Check if transcript contains keyword with word boundary awareness
   * Handles: plurals (engines→engine), word boundaries, case insensitivity
   */
  private matchesKeyword(transcript: string, keyword: string): boolean {
    // Normalize: lowercase
    const normalized = transcript.toLowerCase();
    const keywordLower = keyword.toLowerCase();

    // For multi-word keywords, use simple includes
    // (word boundaries don't work well for phrases like "as received")
    if (keywordLower.includes(' ')) {
      return normalized.includes(keywordLower);
    }

    // For single words, use word boundaries to avoid false positives
    // \b ensures we don't match "winterior" for "interior"
    const patterns = [
      new RegExp(`\\b${keywordLower}\\b`),           // exact match
      new RegExp(`\\b${keywordLower}s\\b`),          // plural (engines)
      new RegExp(`\\b${keywordLower}ing\\b`),        // gerund (steering)
    ];

    return patterns.some(pattern => pattern.test(normalized));
  }

  /**
   * Extract component category from transcript
   */
  private extractComponent(transcript: string): ComponentCategory | undefined {
    const componentKeywords: Record<ComponentCategory, string[]> = {
      engine: ['engine', 'motor', 'block', 'heads', 'intake', 'exhaust manifold', 'v8', 'v6', 'inline', 'powerplant'],
      suspension: ['suspension', 'shock', 'spring', 'control arm', 'sway bar', 'strut', 'a-arm', 'leaf spring', 'coilover'],
      interior: ['interior', 'seat', 'dashboard', 'dash', 'carpet', 'headliner', 'door panel', 'console', 'upholstery', 'trim panel'],
      body: ['body', 'fender', 'hood', 'trunk', 'quarter panel', 'door', 'rocker', 'floor pan', 'firewall', 'cowl'],
      electrical: ['electrical', 'wiring', 'harness', 'gauge', 'light', 'alternator', 'starter', 'battery', 'fuse'],
      drivetrain: ['drivetrain', 'transmission', 'trans', 'differential', 'diff', 'driveshaft', 'clutch', 'axle', 'rear end'],
      brakes: ['brake', 'brakes', 'break', 'breaks', 'rotor', 'caliper', 'brake line', 'master cylinder', 'drum', 'pad', 'shoe'],
      exhaust: ['exhaust', 'header', 'muffler', 'pipe', 'catalytic', 'cat', 'manifold', 'tailpipe'],
      cooling: ['cooling', 'radiator', 'water pump', 'thermostat', 'hose', 'fan', 'coolant', 'overflow'],
      fuel: ['fuel', 'gas tank', 'fuel line', 'carburetor', 'carb', 'fuel injection', 'injector', 'fuel pump'],
      steering: ['steering', 'steering wheel', 'steering column', 'rack', 'power steering', 'tie rod', 'pitman arm'],
      wheels: ['wheel', 'tire', 'rim', 'hubcap', 'wheel bearing', 'lug', 'hub'],
      trim: ['trim', 'chrome', 'molding', 'emblem', 'badge', 'bezel', 'garnish'],
      glass: ['glass', 'windshield', 'window', 'rear glass', 'side glass', 'weatherstrip'],
      other: [],
    };

    for (const [category, keywords] of Object.entries(componentKeywords)) {
      if (keywords.some((keyword) => this.matchesKeyword(transcript, keyword))) {
        return category as ComponentCategory;
      }
    }

    return undefined;
  }

  /**
   * Extract condition state from transcript
   */
  private extractCondition(transcript: string): ConditionState | undefined {
    const conditionKeywords: Record<ConditionState, string[]> = {
      'as-received': [
        'as received', 'as-received', 'when we got it', 'original', 'before',
        'how it came in', 'arrival', 'when it arrived', 'starting point',
        'day one', 'initial', 'untouched'
      ],
      disassembly: [
        'disassembly', 'taking apart', 'teardown', 'tear down', 'disassembled',
        'pulled apart', 'stripped down', 'dismantled'
      ],
      stripped: [
        'stripped', 'bare', 'bare metal', 'media blasted', 'sandblasted',
        'down to metal', 'clean metal'
      ],
      cleaned: [
        'cleaned', 'degreased', 'washed', 'prepped', 'prepared',
        'ready for paint', 'ready for primer'
      ],
      repaired: [
        'repaired', 'fixed', 'welded', 'patched', 'fabricated',
        'metal work', 'bodywork', 'rust repair'
      ],
      primed: [
        'primed', 'primer', 'in primer', 'epoxy', 'sealed',
        'base coat', 'prepped for paint'
      ],
      painted: [
        'painted', 'sprayed', 'color', 'base clear', 'finished paint',
        'wet sanded', 'buffed'
      ],
      restored: [
        'restored', 'after', 'after restoration', 'completed', 'done',
        'finished', 'final', 'now', 'current', 'today'
      ],
      installed: [
        'installed', 'put back', 'reassembled', 'mounted', 'fitted',
        'back together', 'reinstalled', 'bolted up'
      ],
      completed: [
        'completed', 'done', 'finished', 'final result', 'end result',
        'all done', 'wrapped up'
      ],
    };

    // Priority order: more specific states checked first
    // Generic terms like "after" and "finished" are in 'restored' and 'completed'
    // which should only match if no more specific state matches
    const priorityOrder: ConditionState[] = [
      'as-received',    // "before" is common, check specific first
      'disassembly',
      'stripped',
      'cleaned',
      'repaired',
      'primed',
      'painted',
      'installed',
      'restored',       // "after" and "finished" are generic
      'completed',      // Most generic - checked last
    ];

    for (const condition of priorityOrder) {
      const keywords = conditionKeywords[condition];
      if (keywords.some((keyword) => this.matchesKeyword(transcript, keyword))) {
        return condition;
      }
    }

    return undefined;
  }

  /**
   * Get current state
   */
  getState(): VoiceState {
    return this.state;
  }
}

/**
 * Create a singleton voice service instance
 */
let voiceServiceInstance: VoiceService | null = null;

export function getVoiceService(config?: VoiceConfig): VoiceService {
  if (!voiceServiceInstance) {
    voiceServiceInstance = new VoiceService(config);
  }
  return voiceServiceInstance;
}

// Add type declarations for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}
